import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';

const FullscreenPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInStandaloneMode, setIsInStandaloneMode] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    // Check if device is iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iOS);

    // Check if app is already in standalone mode
    const standalone = window.navigator.standalone || 
                      window.matchMedia('(display-mode: standalone)').matches ||
                      window.matchMedia('(display-mode: fullscreen)').matches;
    setIsInStandaloneMode(standalone);

    // Show prompt only on iOS devices not in standalone mode
    const shouldShowPrompt = iOS && !standalone && !localStorage.getItem('hideFullscreenPrompt');
    setShowPrompt(shouldShowPrompt);
  }, []);

  const hidePrompt = () => {
    setShowPrompt(false);
    localStorage.setItem('hideFullscreenPrompt', 'true');
  };

  const requestFullscreen = () => {
    if (document.documentElement.requestFullscreen) {
      document.documentElement.requestFullscreen();
    } else if (document.documentElement.webkitRequestFullscreen) {
      document.documentElement.webkitRequestFullscreen();
    } else if (document.documentElement.mozRequestFullScreen) {
      document.documentElement.mozRequestFullScreen();
    } else if (document.documentElement.msRequestFullscreen) {
      document.documentElement.msRequestFullscreen();
    }
    setShowPrompt(false);
  };

  if (!showPrompt) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-md mx-auto text-center shadow-xl">
        <div className="mb-4">
          <div className="text-4xl mb-2">📱</div>
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            {t('fullscreen.title', 'Better Experience on iPad')}
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            {t('fullscreen.description', 'For the best gaming experience, add this app to your home screen to play in fullscreen mode without Safari\'s address bar.')}
          </p>
        </div>

        {isIOS ? (
          <div className="mb-4">
            <div className="flex items-center justify-center mb-2">
              <div className="bg-blue-100 rounded-lg p-2 mr-2">
                <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm font-medium">
                {t('fullscreen.step1', 'Tap the Share button')}
              </span>
            </div>
            <div className="flex items-center justify-center mb-2">
              <div className="bg-green-100 rounded-lg p-2 mr-2">
                <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 11-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 111.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 111.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm font-medium">
                {t('fullscreen.step2', 'Select "Add to Home Screen"')}
              </span>
            </div>
          </div>
        ) : null}

        <div className="flex gap-2">
          <button
            onClick={hidePrompt}
            className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            {t('fullscreen.later', 'Maybe Later')}
          </button>
          {!isIOS && (
            <button
              onClick={requestFullscreen}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('fullscreen.fullscreen', 'Go Fullscreen')}
            </button>
          )}
          {isIOS && (
            <button
              onClick={hidePrompt}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('fullscreen.gotIt', 'Got it!')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FullscreenPrompt;
